<template>
  <!-- Raised only when a new worker has installed and parked (pwa/appUpdate.ts).
       It sits at the BOTTOM: a module owns the top of the screen, and a bar
       dropped over a module's own header covers the thing the person is
       working on. Above the iOS home indicator via safe-area inset.

       ⚠️ EVERY STRUCTURAL RULE BELOW IS `!important`, ON PURPOSE.
       This bar renders inside the SHELL's <v-app>, but the person looking at it
       is inside a MODULE — which mounts onto document.body and ships its own
       Vuetify 4, flattened out of @layer by `unlayerVuetify()`. Unlayered CSS
       beats layered CSS regardless of specificity, so a module's reset can and
       did win against this component's scoped styles: the flex row collapsed,
       the text wrapped one word per line, and the "Update now" button ended up
       invisible. A grower tapped it on 2026-09-14 and nothing happened, because
       there was nothing there to tap. Same defence the modules use against the
       host (see `metaship-mfe-cold-load-hardening`). -->
  <Transition name="upd">
    <div v-if="appUpdate.ready.value && mode === 'prompt'" class="upd" role="status">
      <p class="upd__text">{{ text }}</p>
      <div class="upd__actions">
        <button type="button" class="upd__btn upd__later" @click="dismissUpdate">{{ t("update.later") }}</button>
        <button type="button" class="upd__btn upd__apply" @click="applyUpdate">{{ t("update.apply") }}</button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { appUpdate, applyUpdate, dismissUpdate, updateMode } from "@/pwa/appUpdate";
import { useSessionStore } from "@/stores/session";

const { t } = useI18n();
const session = useSessionStore();
// Read once on mount: the mode only changes from App info, which reloads
// the page when it applies an update anyway. Auto mode never shows the bar.
const mode = updateMode();

/**
 * Name the app, because "a new version" of WHAT is the question a person
 * actually has when a bar appears over their work. Falls back to the anonymous
 * wording rather than printing an empty name — the tenant is not resolved on
 * every screen this can appear on.
 */
const text = computed(() => {
  const app = session.tenant?.name?.trim();
  return app ? t("update.available", { app }) : t("update.availableAnon");
});
</script>

<style scoped>
.upd {
  position: fixed !important;
  /* Anchored to BOTH edges rather than centred with a transform: a
     shrink-to-fit box is what let the text collapse to one word per line. */
  left: 12px !important;
  right: 12px !important;
  bottom: calc(12px + env(safe-area-inset-bottom, 0px)) !important;
  margin: 0 auto !important;
  z-index: 2400 !important;
  box-sizing: border-box !important;

  display: flex !important;
  flex-direction: column !important;
  align-items: stretch !important;
  gap: 12px !important;
  max-width: 560px !important;
  padding: 14px 16px !important;

  border-radius: 12px !important;
  /* Literal colours with the theme as an OVERRIDE, not as the source. A
     `rgb(var(--v-theme-…))` that does not resolve is an invalid declaration,
     and an invisible button is indistinguishable from a broken one. */
  background: #ffffff !important;
  background: var(--boer-surface, #ffffff) !important;
  color: #1a1a1a !important;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.24) !important;
}

.upd__text {
  flex: none !important;
  min-width: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  font-size: 15px !important;
  line-height: 1.4 !important;
  font-weight: 500 !important;
  color: inherit !important;
}

.upd__actions {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 8px !important;
  flex: none !important;
}

.upd__btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex: 1 1 auto !important;
  /* A thumb in an orchard, not a mouse. 44px is the platform minimum and the
     old 8px/13px buttons were well under it. */
  min-height: 44px !important;
  padding: 0 18px !important;
  border: 0 !important;
  border-radius: 8px !important;
  font-family: inherit !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  line-height: 1 !important;
  white-space: nowrap !important;
  cursor: pointer !important;
  -webkit-appearance: none !important;
  appearance: none !important;
}

.upd__later {
  background: rgba(0, 0, 0, 0.06) !important;
  color: #1a1a1a !important;
}

.upd__apply {
  /* Brand colour when it resolves, a real colour when it does not. */
  background: #0E4D3C !important;
  background: var(--boer-primary, #0E4D3C) !important;
  color: #ffffff !important;
}

.upd__btn:focus-visible {
  outline: 2px solid #0E4D3C !important;
  outline-offset: 2px !important;
}

/* Side by side once there is room for it; stacked is the phone default above. */
@media (min-width: 420px) {
  .upd {
    flex-direction: row !important;
    align-items: center !important;
    gap: 16px !important;
  }
  .upd__text {
    flex: 1 1 auto !important;
  }
  .upd__btn {
    flex: none !important;
  }
}

/* Dark follows the APP's theme, not the phone's.
   `globalState.ts` stamps `data-theme` on <body> from the same toggle the
   grower uses, and Vuetify names its theme the same way. `prefers-color-scheme`
   would put a dark bar over a light app whenever the handset itself is dark —
   which is most handsets. */
body[data-theme="dark"] .upd {
  background: #1E1E1E !important;
  color: #F2F2F2 !important;
  border-color: rgba(255, 255, 255, 0.16) !important;
}

body[data-theme="dark"] .upd__later {
  background: rgba(255, 255, 255, 0.12) !important;
  color: #F2F2F2 !important;
}

.upd-enter-active,
.upd-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease !important;
}

.upd-enter-from,
.upd-leave-to {
  opacity: 0 !important;
  transform: translateY(12px) !important;
}

@media (prefers-reduced-motion: reduce) {
  .upd-enter-active,
  .upd-leave-active {
    transition: none !important;
  }
}
</style>
